import { useState } from 'react'
import { useNavigate } from 'react-router'
import { api } from '../../api/endpoints'
import { errorMessage } from '../../api/client'
import type { User } from '../../api/types'
import { Dialog } from '../../components/Dialog'
import { useSession } from '../../state/session'
import { useUi } from '../../state/ui'
import { storeNewChannel } from './createConversation'
import { nextOnEnter } from './formKeys'
import { UserPicker } from './UserPicker'

export function NewChannelDialog() {
  const open = useUi((s) => s.dialog === 'new-channel')
  const close = useUi((s) => s.closeDialog)
  return open ? <NewChannelForm onClose={close} /> : null
}

function NewChannelForm({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const meId = useSession((s) => s.me?.id)
  const [name, setName] = useState('')
  const [topic, setTopic] = useState('')
  const [members, setMembers] = useState<User[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const trimmed = name.trim()

  const submit = async () => {
    if (!trimmed) {
      setError('Give the channel a name.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const summary = await api.createChannel({ name: trimmed, topic: topic.trim() || undefined, memberIds: members.map((m) => m.id) })
      await storeNewChannel(summary)
      onClose()
      navigate(`/c/${summary.id}`)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      chrome="bar"
      open
      onClose={onClose}
      title="New channel"
      titleId="new-channel-title"
      description="Bring the right people into one conversation."
      className="new-dialog"
      action={
        <button type="submit" form="new-channel-form" className="bar-button bar-action" aria-label="Create channel" disabled={busy || !trimmed}>
          Create
        </button>
      }
    >
      <form
        id="new-channel-form"
        style={{ display: 'contents' }}
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <div className="dialog-body">
          {error ? (
            <p role="alert" className="field-error form-error">
              {error}
            </p>
          ) : null}
          <div className="field">
            <label className="field-label" htmlFor="channel-name">
              Channel name
            </label>
            {/* The cursor starts here rather than in the member search below, which is the first focusable otherwise. */}
            <input
              id="channel-name"
              data-autofocus
              className="text-input"
              value={name}
              maxLength={80}
              placeholder="e.g. Weekend duty managers"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={nextOnEnter}
              enterKeyHint="next"
              autoComplete="off"
              required
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="channel-topic">
              Topic <span className="muted">(optional)</span>
            </label>
            <input
              id="channel-topic"
              className="text-input"
              value={topic}
              maxLength={250}
              placeholder="What is this channel for?"
              onChange={(e) => setTopic(e.target.value)}
              onKeyDown={nextOnEnter}
              enterKeyHint="next"
              autoComplete="off"
            />
          </div>
          <div className="field">
            <span className="field-label">Add people</span>
            <p className="field-help" style={{ marginTop: 0, marginBottom: 10 }}>
              You're included automatically as the owner.
            </p>
            <UserPicker selected={members} onChange={setMembers} excludeIds={meId ? [meId] : []} />
          </div>
        </div>
      </form>
    </Dialog>
  )
}
